import type { GeoArResult } from "../lab-v14/types/GeoArResult";

export type PointIdentifyStatus = "MATCH" | "NO_MATCH" | "WAITING_SENSOR" | "WAITING_POSITION" | "LOADING";

export type PointIdentifyOutcome = {
  status: PointIdentifyStatus;
  result: GeoArResult | null;
  score: number;
  confidenceLabel: "alta" | "media" | "baja" | "sin coincidencia";
  message: string;
  sourceLabel: string | null;
  alternatives: string[];
};

export type PointIdentifyOptions = {
  horizontalAimDegrees?: number;
  verticalAimDegrees?: number;
  minimumConfidence?: number;
  poseStable?: boolean;
  cameraElevationDegrees?: number | null;
};

export function pointIdentifyWaitingPosition(): PointIdentifyOutcome {
  return {
    status: "WAITING_POSITION",
    result: null,
    score: 0,
    confidenceLabel: "sin coincidencia",
    message: "Esperando una posicion GPS de THERA para identificar.",
    sourceLabel: null,
    alternatives: [],
  };
}

export function pointIdentifyLoading(): PointIdentifyOutcome {
  return {
    status: "LOADING",
    result: null,
    score: 0,
    confidenceLabel: "sin coincidencia",
    message: "Buscando elementos geograficos alineados con la reticula...",
    sourceLabel: null,
    alternatives: [],
  };
}

function confidenceLabel(score: number): PointIdentifyOutcome["confidenceLabel"] {
  if (score >= 0.72) return "alta";
  if (score >= 0.48) return "media";
  if (score > 0) return "baja";
  return "sin coincidencia";
}

function normalizedName(value: string | null) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resultKey(result: GeoArResult) {
  const name = normalizedName(result.name);
  const type = result.feature_type ?? "UNKNOWN";
  // Duplicate sources (sidecar + gazetteer) often refer to the same named feature.
  // The first result wins, so callers can put the higher-quality sidecar results first.
  return `${name}|${type}`;
}

export function mergePointIdentifyResults(
  preferred: readonly GeoArResult[],
  secondary: readonly GeoArResult[],
): GeoArResult[] {
  const seen = new Set<string>();
  const merged: GeoArResult[] = [];
  for (const result of [...preferred, ...secondary]) {
    if (!result.feature_uuid || !result.name) continue;
    const key = resultKey(result);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(result);
  }
  return merged;
}


function distancePlausibility(result: GeoArResult) {
  const distance = result.distance ?? Number.POSITIVE_INFINITY;
  switch (result.feature_type) {
    case "RIVER":
      if (distance <= 500) return 1;
      if (distance <= 1_500) return 0.82;
      if (distance <= 3_000) return 0.52;
      if (distance <= 4_000) return 0.25;
      return 0;
    case "PLACE":
      if (distance <= 1_000) return 1;
      if (distance <= 2_500) return 0.72;
      if (distance <= 4_000) return 0.42;
      if (distance <= 5_000) return 0.22;
      return 0;
    case "GEOGRAPHIC_POINT":
    case "VIEWPOINT":
    case "REFUGE":
    case "MOUNTAIN_PASS":
      if (distance <= 1_000) return 1;
      if (distance <= 2_500) return 0.68;
      if (distance <= 4_000) return 0.35;
      if (distance <= 5_000) return 0.18;
      return 0;
    case "LAGOON":
    case "LAKE":
      if (distance <= 2_000) return 1;
      if (distance <= 6_000) return 0.68;
      if (distance <= 10_000) return 0.42;
      if (distance <= 15_000) return 0.22;
      return 0;
    case "HILL":
      if (distance <= 3_000) return 1;
      if (distance <= 10_000) return 0.78;
      if (distance <= 20_000) return 0.5;
      if (distance <= 25_000) return 0.28;
      return 0;
    case "MOUNTAIN":
    case "VOLCANO":
    case "SUMMIT":
      if (distance <= 5_000) return 1;
      if (distance <= 15_000) return 0.82;
      if (distance <= 30_000) return 0.66;
      if (distance <= 50_000) return 0.48;
      return 0;
    default:
      if (distance <= 1_000) return 1;
      if (distance <= 3_000) return 0.55;
      if (distance <= 5_000) return 0.2;
      return 0;
  }
}

function resultSource(result: GeoArResult | null) {
  if (!result) return null;
  return result.data_source || (String(result.feature_uuid ?? "").startsWith("gn-") ? "THERA Gazetteer" : "Geo AR sidecar");
}

export function identifyPointedFeature(
  results: readonly GeoArResult[],
  options: PointIdentifyOptions = {},
): PointIdentifyOutcome {
  const horizontalAimDegrees = Math.max(2, options.horizontalAimDegrees ?? 8);
  const verticalAimDegrees = Math.max(3, options.verticalAimDegrees ?? 12);
  const minimumConfidence = Math.min(1, Math.max(0, options.minimumConfidence ?? 0.24));

  if (options.poseStable === false) {
    return {
      status: "WAITING_SENSOR",
      result: null,
      score: 0,
      confidenceLabel: "sin coincidencia",
      message: "Sosten el telefono apuntando con la camara hacia el destino; evita dejarlo plano hacia el suelo o cielo.",
      sourceLabel: null,
      alternatives: [],
    };
  }

  const hasHeading = results.some((result) => result.relative_angle !== null);
  if (!hasHeading) {
    return {
      status: "WAITING_SENSOR",
      result: null,
      score: 0,
      confidenceLabel: "sin coincidencia",
      message: "Esperando rumbo del telefono para identificar.",
      sourceLabel: null,
      alternatives: [],
    };
  }

  const candidates = results
    .filter((result) => result.feature_uuid !== null && result.visibility_state !== "TOO_FAR" && result.visibility_state !== "NO_INFORMATION")
    .map((result) => {
      const horizontal = Math.abs(result.relative_angle ?? Number.POSITIVE_INFINITY);
      const vertical = result.relative_vertical_angle === null ? 0 : Math.abs(result.relative_vertical_angle);
      const horizontalScore = Math.max(0, 1 - horizontal / horizontalAimDegrees);
      const verticalScore = result.relative_vertical_angle === null
        ? 0.72
        : Math.max(0, 1 - vertical / verticalAimDegrees);
      const alignmentScore = horizontalScore * 0.72 + verticalScore * 0.28;
      const proximityScore = distancePlausibility(result);
      const score = alignmentScore * 0.48 + proximityScore * 0.30 + result.confidence * 0.22;
      return { result, horizontal, vertical, proximityScore, score };
    })
    .filter((candidate) => candidate.horizontal <= horizontalAimDegrees)
    .filter((candidate) => candidate.result.relative_vertical_angle === null || candidate.vertical <= verticalAimDegrees)
    .filter((candidate) => candidate.proximityScore > 0)
    .sort((a, b) => b.score - a.score
      || b.proximityScore - a.proximityScore
      || (a.result.distance ?? Infinity) - (b.result.distance ?? Infinity));

  const best = candidates[0];
  if (!best || best.score < minimumConfidence) {
    return {
      status: "NO_MATCH",
      result: null,
      score: best?.score ?? 0,
      confidenceLabel: "sin coincidencia",
      message: "No hay un elemento geografico conocido suficientemente alineado con la reticula.",
      sourceLabel: null,
      alternatives: candidates.slice(0, 3).map((candidate) => candidate.result.name).filter((name): name is string => Boolean(name)),
    };
  }

  const second = candidates[1];
  const ambiguous = Boolean(second && Math.abs(best.score - second.score) < 0.06);
  const label = ambiguous && confidenceLabel(best.score) === "alta" ? "media" : confidenceLabel(best.score);
  const alternatives = candidates
    .slice(1, 4)
    .map((candidate) => candidate.result.name)
    .filter((name): name is string => Boolean(name));

  return {
    status: "MATCH",
    result: best.result,
    score: Math.round(best.score * 1000) / 1000,
    confidenceLabel: label,
    message: ambiguous
      ? `${best.result.name ?? "Elemento geografico"} es la mejor coincidencia, pero hay otro elemento cercano en la misma direccion.`
      : `${best.result.name ?? "Elemento geografico"} alineado con la reticula.`,
    sourceLabel: resultSource(best.result),
    alternatives,
  };
}
